import { pipeSlope, invertOf } from './sewer';
import { mPerLat, mPerLon } from './geo';
import { useEffect, useState } from 'react';
import { buildGraph } from './netGraph';
import { haversine, resolvePt } from './model';

// shared UI state: open-end check toggle, shut-off analysis (arm + result)
let st = { ends: false, arm: false, shut: null };
const subs = new Set();
export const getNc = () => st;
export const setNc = (p) => { st = { ...st, ...p }; subs.forEach((f) => f(st)); };
export function useNc() {
  const [v, setV] = useState(st);
  useEffect(() => { subs.add(setV); return () => subs.delete(setV); }, []);
  return v;
}

const TOL = 0.3;
// pipe ends that are neither a component nor shared with another pipe vertex
export function openEnds(pipes = [], fittings = []) {
  const all = pipes.map((p) => ({ p, P: (p.points || []).map((x) => resolvePt(x, fittings)) }));
  const out = [];
  all.forEach(({ p, P }) => {
    if (P.length < 2) return;
    [0, P.length - 1].forEach((i) => {
      if (p.points[i]?.fid || !P[i]) return;
      const linked = all.some((o) => o.P.some((q, j) => q && !(o.p === p && j === i) && haversine(q, P[i]) <= TOL));
      if (!linked) out.push({ pipeId: p.id, i, pt: P[i], name: p.name });
    });
  });
  return out;
}

export const isValve = (f) => f?.type === 'valve';
export const isHouse = (f) => f?.type === 'house' || ['HA', 'HAE', 'HS'].includes(f?.sym);

// area cut off by closing valve v: flood the graph until the next valves
export function shutArea(pipes, fittings, valveId, opened = []) {
  const G = buildGraph(pipes, fittings), fx = Object.fromEntries(fittings.map((f) => [f.id, f]));
  const start = 'f:' + valveId, seen = new Set([start]), eSeen = new Set(), q = [start];
  const edges = [], bnd = new Set(), nodes = new Set(), housePipes = new Set();
  let len = 0;
  while (q.length) {
    const u = q.shift();
    (G.adj[u] || []).forEach((e) => {
      const id = [u, e.to].sort().join('|') + e.pipe.id;
      if (eSeen.has(id)) return;
      eSeen.add(id);
      edges.push([G.pos[u], G.pos[e.to]]);
      len += e.len;
      if (e.pipe.type === 'house') housePipes.add(e.pipe.id);
      if (seen.has(e.to)) return;
      seen.add(e.to);
      const f = e.to.startsWith('f:') ? fx[e.to.slice(2)] : null;
      if (f) nodes.add(f.id);
      if (isValve(f) && !opened.includes(f.id)) bnd.add(f.id); else q.push(e.to);
    });
  }
  const leaves = [...seen].filter((k) => (G.adj[k] || []).length === 1 && !k.startsWith('f:')).length;
  const list = (fn) => [...nodes].map((id) => fx[id]).filter(fn).map((f) => f.id);
  return { valveId, opened: opened.filter((id) => nodes.has(id)), edges, len, valves: [...bnd], houses: list(isHouse), hydrants: list((f) => f.type === 'hydrant'), housePipes: housePipes.size, open: leaves > 0, empty: !G.adj[start] };
}

// metres from point p to segment a-b (local plane)
const segDist = (p, a, b) => {
  const k = mPerLon(p[0]), X = (q) => [(q[1] - p[1]) * k, (q[0] - p[0]) * mPerLat(p[0])];
  const [ax, ay] = X(a), [bx, by] = X(b), dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy;
  const u = L2 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / L2)) : 0;
  return Math.hypot(ax + u * dx, ay + u * dy);
};
// garden irrigation parts that must hang on a line (sprinklers, drippers, valves, controller side)
const IRR = ['g03', 'g04', 'g05', 'g27', 'g28', 'g29', 'g30', 'g31', 'g35', 'g36', 'g37', 'g38', 'g39', 'g40'];
const NET = ['valve', 'hydrant', 'manhole', 'house', 'air', 'drain', 'cross4', 'tee3', 'meter'];

// network health: open ends, missing tees, unconnected components, pipes without DN -> score 0-100
export function netQuality(project) {
  const pipes = project?.pipes || [], fits = project?.fittings || [];
  const issues = [];
  openEnds(pipes, fits).forEach((o) => {
    const onPipe = pipes.some((pp) => pp.id !== o.pipeId && (() => { const P = pp.points.map((x) => resolvePt(x, fits)); return P.some((a, i) => i && a && P[i - 1] && segDist(o.pt, P[i - 1], a) <= 0.5); })());
    issues.push({ kind: onPipe ? 'tee' : 'open', w: 1, pt: o.pt, pipeId: o.pipeId, i: o.i, name: o.name });
  });
  const used = new Set(pipes.flatMap((pp) => pp.points.filter((x) => x?.fid).map((x) => x.fid)));
  const netFits = fits.filter((f) => (NET.includes(f.type) || IRR.includes(f.catId)) && !f.privUnknown);
  netFits.filter((f) => !used.has(f.id)).forEach((f) => issues.push({ kind: 'lonely', w: 1, pt: [f.lat, f.lon], fitId: f.id, name: f.name }));
  pipes.filter((pp) => !pp.private && !String(pp.dn || '').trim()).forEach((pp) => issues.push({ kind: 'nodn', w: 0.5, pipeId: pp.id, name: pp.name, pt: resolvePt(pp.points[0], fits) }));
  // sewer: slope, missing manhole levels, condition class 0/1
  pipes.filter((pp) => pp.type === 'sewer').forEach((pp) => {
    const s = pipeSlope(pp, fits), pt = resolvePt(pp.points[0], fits);
    if (s?.status === 'neg' || s?.status === 'low') issues.push({ kind: s.status === 'neg' ? 'swneg' : 'swlow', w: s.status === 'neg' ? 1 : 0.5, pipeId: pp.id, name: pp.name, pt });
    const w = pp.insp?.items?.length ? Math.min(...pp.insp.items.map((d) => +d.cls)) : null;
    if (w != null && w <= 1) issues.push({ kind: 'swzk', w: 1, pipeId: pp.id, name: `${pp.name} · ZK ${w}`, pt });
  });
  fits.filter((f) => f.type === 'manhole' && invertOf(f) == null).forEach((f) => issues.push({ kind: 'swlev', w: 0.3, pt: [f.lat, f.lon], fitId: f.id, name: f.name }));
  const total = pipes.length * 2.5 + netFits.length;
  const pen = issues.reduce((s, x) => s + x.w, 0);
  return { score: total ? Math.max(0, Math.round(100 * (1 - pen / total))) : 100, issues, empty: !total };
}
